import type { CountdownProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { CheckboxField, ColorField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const ALIGN = ["start", "center", "end"] as const;

function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  // yyyy-MM-ddTHH:mm (local — what <input type="datetime-local"> expects).
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CountdownInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as CountdownProps;

  return (
    <>
      <div className="emke-inspector-field">
        <label>Target date / time</label>
        <input
          type="datetime-local"
          value={toLocalInputValue(p.target)}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return;
            const d = new Date(v);
            if (!Number.isNaN(d.getTime())) {
              update(widget.id, { target: d.toISOString() });
            }
          }}
          style={{ width: "100%" }}
        />
      </div>
      <TextField
        label="Title"
        value={p.title ?? ""}
        onChange={(v) => update(widget.id, { title: v || undefined })}
      />
      <TextField
        label="Expired message"
        value={p.expiredMessage ?? ""}
        onChange={(v) => update(widget.id, { expiredMessage: v || undefined })}
      />
      <CheckboxField
        label="Show seconds"
        value={p.showSeconds ?? true}
        onChange={(v) => update(widget.id, { showSeconds: v })}
      />
      <div className="emke-inspector-grid-2" style={{ padding: "0 16px" }}>
        <TextField
          label="Days label"
          value={p.daysLabel ?? ""}
          onChange={(v) => update(widget.id, { daysLabel: v || undefined })}
        />
        <TextField
          label="Hours label"
          value={p.hoursLabel ?? ""}
          onChange={(v) => update(widget.id, { hoursLabel: v || undefined })}
        />
      </div>
      <div className="emke-inspector-grid-2" style={{ padding: "0 16px" }}>
        <TextField
          label="Minutes label"
          value={p.minutesLabel ?? ""}
          onChange={(v) => update(widget.id, { minutesLabel: v || undefined })}
        />
        <TextField
          label="Seconds label"
          value={p.secondsLabel ?? ""}
          onChange={(v) => update(widget.id, { secondsLabel: v || undefined })}
        />
      </div>
      <SelectField
        label="Align"
        value={p.align ?? "center"}
        options={ALIGN}
        onChange={(v) => update(widget.id, { align: v as CountdownProps["align"] })}
      />
      <ColorField
        label="Digit colour"
        value={p.digitColor ?? "#111827"}
        onChange={(v) => update(widget.id, { digitColor: v })}
      />
      <ColorField
        label="Label colour"
        value={p.labelColor ?? "#6b7280"}
        onChange={(v) => update(widget.id, { labelColor: v })}
      />
    </>
  );
}
